import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation, Outlet } from 'react-router';
import { useSelector } from 'react-redux';

/**
 * AnalyticsFeatureGuard component to protect analytics routes based on feature flag
 * Redirects users to appropriate pages if analytics feature is disabled
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if feature is enabled
 * @param {string} props.redirectPath - Optional custom redirect path (defaults based on route type)
 */
const AnalyticsFeatureGuard = ({ children, redirectPath }) => {
  const location = useLocation();
  const featureManagementData = useSelector(
    (state) => state.featureManagementData
  );

  const isFeatureFlagsLoaded =
    featureManagementData && !Array.isArray(featureManagementData);
  const isAnalyticsFeatureEnabled =
    isFeatureFlagsLoaded &&
    featureManagementData['isAnalyticsFeatureEnabled'] === true;

  if (!isFeatureFlagsLoaded) {
    return null;
  }

  // If feature is disabled, redirect to appropriate page
  if (!isAnalyticsFeatureEnabled) {
    // If a custom redirect path is provided, use it
    if (redirectPath) {
      return <Navigate to={redirectPath} replace />;
    }

    // Otherwise, determine redirect based on current path
    // For external analytics (/ext/analytics), redirect to PSQ
    if (location.pathname.includes('/ext/analytics')) {
      return <Navigate to="/ext/psq" replace />;
    }

    // For internal client analytics (/client/:id/analytics), redirect to client page
    const clientRegex = /\/client\/(\d+)/i;
    const match = location.pathname.match(clientRegex);
    if (match) {
      const clientId = match[1];
      return <Navigate to={`/client/${clientId}`} replace />;
    }

    // Default fallback to home
    return <Navigate to="/" replace />;
  }

  // If feature is enabled, render the children components or Outlet for nested routes
  return children || <Outlet />;
};

AnalyticsFeatureGuard.propTypes = {
  children: PropTypes.node,
  redirectPath: PropTypes.string,
};

export default AnalyticsFeatureGuard;
